import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Card } from 'react-bootstrap';
import { BryntumSchedulerPro } from '@bryntum/schedulerpro-react-thin';
import type { ColumnConfig } from '@bryntum/grid-thin';
import type { Model } from '@bryntum/core-thin';

import '../../../../../styles/bryntum/bryntum-schedulerpro.css';
import './styles.css';
import { renderShiftEvent } from './eventRenderer';
import type { OpenShiftLabels } from './eventRenderer';
import { renderResourceCell } from './resourceRenderer';
import ColleaguesToolbar from './ColleaguesToolbar';
import { useColleaguesCalendarData } from './useColleaguesCalendarData';
import { getFillTicks, getViewPreset } from './utils';
import { useCurrentLocale } from '../../../../../context/I18n';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { SchedulerLoading } from './loading/SchedulerLoading';
import { useSchedulerSetup } from './useSchedulerSetup';
import { useSchedulerNavigation } from './useSchedulerNavigation';
import { useOpenShiftActions } from '../components/useOpenShiftActions';
import { ColleaguesCalendarShiftsDataDocument } from '../../../__generated__/graphql';
import type { OpenShiftEventData } from '../components/mapOpenShiftsToEvents';
import ModalSidebar, {
  ModalSidebarContent,
} from '../../../components/ModalSidebar';
import { OpenShiftSidePanel } from '../components/OpenShiftSidePanel';
import {
  getOpenShiftStateLabels,
  type SelectedOpenShift,
} from '../components/openShiftConstants';
import { useFlairLocalStorage } from '../../../hooks/useFlairLocalStorage';

const RESOURCE_COLUMN: ColumnConfig = {
  field: 'name',
  text: '',
  autoWidth: true,
  minWidth: '10rem',
  renderer: ({ record }: { record: Model }) => renderResourceCell(record),
  filterable: true,
};

export const ColleaguesCalendar: React.FC = () => {
  const locale = useCurrentLocale();
  const schedulerRef = useRef<BryntumSchedulerPro>(null);
  const tOpenShift = useNamespacedTranslation('schedulerShifts.openShift');

  const [showOpenShifts, setShowOpenShifts] = useFlairLocalStorage(
    'colleagues_calendar_show_open_shifts',
    true,
  );

  const [selectedOpenShift, setSelectedOpenShift] =
    useState<SelectedOpenShift | null>(null);

  const openShiftLabels: OpenShiftLabels = useMemo(
    () => ({
      stateLabels: getOpenShiftStateLabels(tOpenShift),
      pendingLabels: {
        requesting: tOpenShift('requesting'),
        canceling: tOpenShift('canceling'),
      },
    }),
    [tOpenShift],
  );

  const {
    view,
    currentDate,
    startDate,
    endDate,
    schedulers,
    effectiveScheduler,
    allTargets,
    effectiveTargetIds,
    resources,
    events,
    openShiftsCount,
    initialLoading,
    dataLoading,
    handleSchedulerChange,
    handleTargetsChange,
    handleViewChange: onViewChange,
    handleDateChange,
  } = useColleaguesCalendarData(showOpenShifts);

  useSchedulerSetup(schedulerRef, {
    locale,
    resources,
    initialLoading,
    startDate,
    endDate,
  });

  const {
    handleViewChange,
    handleNavigatePrevious,
    handleNavigateNext,
    handleNavigateToday,
  } = useSchedulerNavigation(schedulerRef, {
    view,
    currentDate,
    onViewChange,
    onDateChange: handleDateChange,
  });

  const closeOpenShiftSidebar = useCallback(() => {
    setSelectedOpenShift(null);
  }, []);

  const { requestShift, cancelRequest, pendingAction } = useOpenShiftActions(
    ColleaguesCalendarShiftsDataDocument,
    closeOpenShiftSidebar,
  );

  const openShiftLabel = tOpenShift('label');

  const handleEventClick = useCallback(
    ({ eventRecord }: { eventRecord: Model }) => {
      if (!eventRecord.getData('isOpenShift')) return;

      const get = <K extends keyof OpenShiftEventData>(
        key: K,
      ): OpenShiftEventData[K] => eventRecord.getData(key);

      setSelectedOpenShift({
        name: openShiftLabel,
        startDate: (get('startDate') as Date).toISOString(),
        endDate: (get('endDate') as Date).toISOString(),
        targetName: get('targetName'),
        breakPeriodMinutes: get('breakPeriodMinutes') || 0,
        state: get('openShiftState'),
        notAvailableReason: get('openShiftNotAvailableReason'),
        openShiftId: get('openShiftId'),
        assignmentRequestId: get('assignmentRequestId'),
      });
    },
    [openShiftLabel],
  );

  return (
    <Card className="d-flex flex-column">
      <Card.Header className="px-3 py-0">
        <ColleaguesToolbar
          view={view}
          onViewChange={handleViewChange}
          schedulers={schedulers}
          selectedScheduler={effectiveScheduler}
          onSchedulerChange={handleSchedulerChange}
          targets={allTargets}
          selectedTargetIds={effectiveTargetIds}
          onTargetsChange={handleTargetsChange}
          onNavigatePrevious={handleNavigatePrevious}
          onNavigateNext={handleNavigateNext}
          onNavigateToday={handleNavigateToday}
          loading={initialLoading}
          showOpenShifts={showOpenShifts}
          onShowOpenShiftsChange={setShowOpenShifts}
          openShiftsCount={openShiftsCount}
        />
      </Card.Header>
      <div className="flex-grow-1 min-h-0 position-relative scheduler-height">
        {initialLoading ? (
          <SchedulerLoading />
        ) : (
          <>
            <BryntumSchedulerPro
              ref={schedulerRef}
              resources={resources}
              events={events}
              viewPreset={getViewPreset(view)}
              columns={[RESOURCE_COLUMN] as ColumnConfig[]}
              barMargin={7}
              rowHeight={70}
              eventRenderer={({ eventRecord }) =>
                renderShiftEvent(eventRecord, openShiftLabels)
              }
              onEventClick={handleEventClick}
              eventDragFeature={false}
              eventResizeFeature={false}
              eventEditFeature={false}
              eventCopyPasteFeature={false}
              eventDragCreateFeature={false}
              eventDragSelectFeature={false}
              dependenciesFeature={false}
              taskEditFeature={false}
              eventMenuFeature={{
                items: { deleteEvent: false, splitEvent: false },
              }}
              scheduleMenuFeature={{
                items: { addEvent: false },
              }}
              cellMenuFeature={{ items: { removeRow: false } }}
              filterBarFeature={{ compactMode: true }}
              zoomOnMouseWheel={false}
              fillTicks={getFillTicks(view)}
              onScheduleDblClick={() => false}
            />
            {dataLoading && (
              <div className="position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-75">
                <SchedulerLoading />
              </div>
            )}
          </>
        )}
      </div>
      <ModalSidebar
        show={!!selectedOpenShift}
        onClose={() => setSelectedOpenShift(null)}
        header={selectedOpenShift?.name || ''}
        content={
          selectedOpenShift && (
            <ModalSidebarContent
              body={
                <OpenShiftSidePanel
                  name={selectedOpenShift.name}
                  startDate={selectedOpenShift.startDate}
                  endDate={selectedOpenShift.endDate}
                  targetName={selectedOpenShift.targetName}
                  breakPeriodMinutes={selectedOpenShift.breakPeriodMinutes}
                  state={selectedOpenShift.state}
                  notAvailableReason={selectedOpenShift.notAvailableReason}
                  onRequest={() => requestShift(selectedOpenShift.openShiftId)}
                  onCancelRequest={() => {
                    if (selectedOpenShift.assignmentRequestId) {
                      cancelRequest(
                        selectedOpenShift.openShiftId,
                        selectedOpenShift.assignmentRequestId,
                      );
                    }
                  }}
                  isPending={
                    pendingAction?.openShiftId === selectedOpenShift.openShiftId
                  }
                />
              }
            />
          )
        }
      />
    </Card>
  );
};
